/**
 * Dev-only MapLibre layers displaying bus route shapes (lines and direction arrows).
 */

import type { Map as MapLibre } from "maplibre-gl";

import type { CompactTimeline } from "../../../model/realtime/CompactTimeline";
import type { Vehicle } from "../../../model/Vehicle";
import { resolveVehicleRouteDisplayColor } from "../../../tools/ColorTools";
import {
    timelineToArrowFeatures,
    timelineToLineCoordinates,
    timelineToPointFeatures,
} from "../../../tools/compactTimelineDisplay.tools";
import { VEHICLE_CUSTOM_LAYER_ID } from "../mapLayerIds";
import {
    buildBusRouteDebugIds,
    BUS_ROUTE_DEBUG_LAYER_PREFIXES,
    BUS_ROUTE_DEBUG_SOURCE_PREFIXES,
} from "./mapDebugLayerIds";

const BUS_ROUTE_LINE_WIDTH = 3;

const BUS_ROUTE_ARROW_LINE_WIDTH = 2;

export interface SyncBusRouteShapesParams {

    /** Vehicles to visualize (typically all tracked vehicles). */
    "vehicles": Iterable<Vehicle>;

    /** Timeline lookup by vehicle id. */
    "getTimeline": ( vehicleId: string ) => CompactTimeline | undefined;

    /** When set, only this vehicle is drawn. */
    "singleVehicleId"?: string;
}

/**
 * Removes all bus route shape debug sources and layers from the map.
 *
 * @param map Target MapLibre map.
 */
export const removeBusRouteShapesFromMap = ( map: MapLibre | undefined ): void => {
    if ( map === undefined ) {
        return;
    }

    const mapStyle = map.getStyle();
    const layerIds = ( mapStyle.layers ?? [] ).map( ( layer ) => layer.id );
    const sourceIds = Object.keys( mapStyle.sources ?? {} );

    layerIds.
        filter( ( id ) => BUS_ROUTE_DEBUG_LAYER_PREFIXES.some( ( prefix ) => id.startsWith( prefix ) ) ).
        forEach( ( id ) => {
            if ( map.getLayer( id ) != null ) {
                map.removeLayer( id );
            }
        } );

    sourceIds.
        filter( ( id ) => BUS_ROUTE_DEBUG_SOURCE_PREFIXES.some( ( prefix ) => id.startsWith( prefix ) ) ).
        forEach( ( id ) => {
            if ( map.getSource( id ) != null ) {
                map.removeSource( id );
            }
        } );
};

/**
 * Builds a LineString GeoJSON feature collection from a compact timeline.
 *
 * @param vehicle Vehicle metadata.
 * @param timeline Cached columnar timeline.
 * @returns GeoJSON FeatureCollection for the route line.
 */
const buildTimelineLineGeoJSON = (
    vehicle: Vehicle,
    timeline: CompactTimeline,
): GeoJSON.FeatureCollection<GeoJSON.LineString> => {
    const coordinates = timelineToLineCoordinates( timeline );
    if ( coordinates.length < 2 ) {
        return {
            "type":     "FeatureCollection",
            "features": [],
        };
    }

    return {
        "type":     "FeatureCollection",
        "features": [
            {
                "type":       "Feature",
                "properties": {
                    "vehicleId":    vehicle.id,
                    "vehicleLabel": vehicle.label,
                    "segmentIndex": 0,
                },
                "geometry": {
                    "type": "LineString",
                    coordinates,
                },
            },
        ],
    };
};

/**
 * Builds point GeoJSON for debug map layers from a compact timeline.
 *
 * @param vehicle Vehicle metadata.
 * @param timeline Cached columnar timeline.
 * @returns GeoJSON FeatureCollection of points.
 */
const buildTimelinePointGeoJSON = (
    vehicle: Vehicle,
    timeline: CompactTimeline,
): GeoJSON.FeatureCollection<GeoJSON.Point> => ( {
    "type":     "FeatureCollection",
    "features": timelineToPointFeatures( vehicle.id, vehicle.label, timeline ),
} );

/**
 * Builds arrow GeoJSON for debug map layers from a compact timeline.
 *
 * @param vehicle Vehicle metadata.
 * @param timeline Cached columnar timeline.
 * @returns GeoJSON FeatureCollection of direction arrows.
 */
const buildTimelineArrowGeoJSON = (
    vehicle: Vehicle,
    timeline: CompactTimeline,
): GeoJSON.FeatureCollection<GeoJSON.MultiLineString> => ( {
    "type":     "FeatureCollection",
    "features": timelineToArrowFeatures( vehicle.id, timeline ),
} );

/**
 * Adds or refreshes bus route shape line and arrow layers for all eligible vehicles.
 *
 * @param map Target MapLibre map.
 * @param params Vehicles, timelines, and optional single-vehicle filter.
 */
export const syncBusRouteShapesOnMap = (
    map: MapLibre | undefined,
    params: SyncBusRouteShapesParams,
): void => {
    if ( map === undefined ) {
        return;
    }

    removeBusRouteShapesFromMap( map );

    const beforeId = map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) != null ? VEHICLE_CUSTOM_LAYER_ID : undefined;

    for ( const vehicle of params.vehicles ) {
        const {
            lineSourceId,
            lineLayerId,
            pointSourceId,
            pointLayerId,
            arrowSourceId,
            arrowLayerId,
        } = buildBusRouteDebugIds( vehicle.id );

        if ( params.singleVehicleId !== undefined && vehicle.id !== params.singleVehicleId ) {
            continue;
        }

        const timeline = params.getTimeline( vehicle.id );
        if ( timeline === undefined || timeline.length < 2 ) {
            console.warn( `[busRouteShapesDebugLayers] no timeline for ${ vehicle.id }` );

            continue;
        }

        const displayRouteColor = resolveVehicleRouteDisplayColor( vehicle.color );
        const lineColor = `rgb(${ displayRouteColor.r },${ displayRouteColor.g },${ displayRouteColor.b })`;

        const lineGeojson = buildTimelineLineGeoJSON( vehicle, timeline );
        const pointGeojson = buildTimelinePointGeoJSON( vehicle, timeline );
        const arrowGeojson = buildTimelineArrowGeoJSON( vehicle, timeline );

        if ( map.getLayer( pointLayerId ) != null ) {
            map.removeLayer( pointLayerId );
        }
        if ( map.getLayer( lineLayerId ) != null ) {
            map.removeLayer( lineLayerId );
        }
        if ( map.getLayer( arrowLayerId ) != null ) {
            map.removeLayer( arrowLayerId );
        }
        if ( map.getSource( pointSourceId ) != null ) {
            map.removeSource( pointSourceId );
        }
        if ( map.getSource( lineSourceId ) != null ) {
            map.removeSource( lineSourceId );
        }
        if ( map.getSource( arrowSourceId ) != null ) {
            map.removeSource( arrowSourceId );
        }

        map.addSource( lineSourceId, {
            "type": "geojson",
            "data": lineGeojson,
        } );

        map.addLayer( {
            "id":     lineLayerId,
            "type":   "line",
            "source": lineSourceId,
            "paint":  {
                "line-color": lineColor,
                "line-width": BUS_ROUTE_LINE_WIDTH,
            },
        }, beforeId );

        map.addSource( pointSourceId, {
            "type": "geojson",
            "data": pointGeojson,
        } );

        map.addSource( arrowSourceId, {
            "type": "geojson",
            "data": arrowGeojson,
        } );

        map.addLayer( {
            "id":     arrowLayerId,
            "type":   "line",
            "source": arrowSourceId,
            "layout": {
                "line-cap":  "round",
                "line-join": "round",
            },
            "paint": {
                "line-color": lineColor,
                "line-width": BUS_ROUTE_ARROW_LINE_WIDTH,
            },
        }, beforeId );
    }
};
